import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Loader2, DollarSign, Lock } from "lucide-react";
import { pretty, LANES } from "./crmConstants";

// Revenue statuses surfaced in the quick action (internal-only outcome capture).
const REV_OPTIONS = ["pending", "invoice_needed", "invoiced", "partially_received", "received", "closed_no_revenue"];

const EMPTY = {
  service_lane: "", relationship_id: "", territory_id: "", franchise_id: "",
  estimated_value: "", actual_value: "", revenue_status: "received",
  expected_close_date: "", actual_close_date: "", opened_by_user_id: "", managed_by_user_id: "",
  closed_by_user_id: "", notes: "",
};

const Field = ({ label, children }) => (
  <div><label className="text-[11px] text-[#4B5563]">{label}</label>{children}</div>
);

export default function RevenueOutcomeDialog({ open, onOpenChange, leadId, opportunityId, defaults = {}, contextLabel, onSaved }) {
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({ ...EMPTY, ...Object.fromEntries(Object.entries(defaults).filter(([, v]) => v != null && v !== "").map(([k, v]) => [k, String(v)])) });
    api.get("/crm/opportunities/config").then(({ data }) => setCfg(data)).catch(() => setCfg({ staff: [], territories: [], franchises: [], relationships: [] }));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const staffSel = (key, testid) => (
    <Select value={form[key] || "none"} onValueChange={(v) => setForm({ ...form, [key]: v === "none" ? "" : v })}>
      <SelectTrigger data-testid={testid} className="h-9 text-[13px]"><SelectValue placeholder="—" /></SelectTrigger>
      <SelectContent><SelectItem value="none">—</SelectItem>{(cfg?.staff || []).map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
    </Select>
  );

  const submit = async () => {
    setSaving(true);
    try {
      const payload = {
        lead_id: leadId || null, opportunity_id: opportunityId || null,
        name: defaults.name || null,
        service_lane: form.service_lane || null,
        relationship_id: form.relationship_id || null,
        territory_id: form.territory_id || null, franchise_id: form.franchise_id || null,
        estimated_value: form.estimated_value === "" ? null : Number(form.estimated_value),
        actual_value: form.actual_value === "" ? null : Number(form.actual_value),
        revenue_status: form.revenue_status || null,
        expected_close_date: form.expected_close_date || null,
        actual_close_date: form.actual_close_date || null,
        opened_by_user_id: form.opened_by_user_id || null,
        managed_by_user_id: form.managed_by_user_id || null,
        closed_by_user_id: form.closed_by_user_id || null,
        notes: form.notes || null,
      };
      const { data } = await api.post("/crm/revenue-outcome", payload);
      toast.success(data.action === "created" ? "Revenue outcome recorded (new deal)." : "Revenue outcome updated.");
      onOpenChange(false);
      onSaved && onSaved(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to record outcome."); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="revenue-outcome-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-[#1C4358] flex items-center gap-2"><DollarSign size={18} className="text-[#2F7D5A]" /> Add Revenue Outcome</DialogTitle>
        </DialogHeader>
        {!cfg ? <div className="flex justify-center py-8"><Loader2 className="animate-spin text-[#1C4358]" size={24} /></div> : (
          <>
            {contextLabel && <div className="text-[12px] text-[#4B5563] -mt-1 mb-1">Linked to <span className="font-semibold text-[#1C4358]">{contextLabel}</span></div>}
            <div className="grid sm:grid-cols-2 gap-3 max-h-[58vh] overflow-y-auto pr-1">
              <Field label="Service lane">
                <Select value={form.service_lane || "none"} onValueChange={(v) => setForm({ ...form, service_lane: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="rev-service-lane" className="h-9 text-[13px]"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">—</SelectItem>{LANES.map((l) => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Partner / vendor involved">
                <Select value={form.relationship_id || "none"} onValueChange={(v) => setForm({ ...form, relationship_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="rev-relationship" className="h-9 text-[13px]"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">—</SelectItem>{(cfg.relationships || []).map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Territory">
                <Select value={form.territory_id || "none"} onValueChange={(v) => setForm({ ...form, territory_id: v === "none" ? "" : v })}>
                  <SelectTrigger className="h-9 text-[13px]"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">—</SelectItem>{(cfg.territories || []).map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Franchise">
                <Select value={form.franchise_id || "none"} onValueChange={(v) => setForm({ ...form, franchise_id: v === "none" ? "" : v })}>
                  <SelectTrigger className="h-9 text-[13px]"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">—</SelectItem>{(cfg.franchises || []).map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Estimated value ($)"><Input data-testid="rev-estimated" type="number" value={form.estimated_value} onChange={(e) => setForm({ ...form, estimated_value: e.target.value })} className="h-9 text-[14px]" /></Field>
              <Field label="Actual value ($)"><Input data-testid="rev-actual" type="number" value={form.actual_value} onChange={(e) => setForm({ ...form, actual_value: e.target.value })} className="h-9 text-[14px]" /></Field>
              <Field label="Revenue status">
                <Select value={form.revenue_status} onValueChange={(v) => setForm({ ...form, revenue_status: v })}>
                  <SelectTrigger data-testid="rev-status" className="h-9 text-[13px]"><SelectValue /></SelectTrigger>
                  <SelectContent>{REV_OPTIONS.map((s) => <SelectItem key={s} value={s}>{pretty(s)}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <div />
              <Field label="Expected payment date"><Input type="date" value={form.expected_close_date} onChange={(e) => setForm({ ...form, expected_close_date: e.target.value })} className="h-9 text-[13px]" /></Field>
              <Field label="Actual received date"><Input type="date" value={form.actual_close_date} onChange={(e) => setForm({ ...form, actual_close_date: e.target.value })} className="h-9 text-[13px]" /></Field>
              <Field label="Opened by">{staffSel("opened_by_user_id", "rev-opened-by")}</Field>
              <Field label="Managed by">{staffSel("managed_by_user_id", "rev-managed-by")}</Field>
              <Field label="Closed by">{staffSel("closed_by_user_id", "rev-closed-by")}</Field>
              <div />
              <div className="sm:col-span-2"><Field label="Notes"><Textarea data-testid="rev-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="text-[14px]" /></Field></div>
            </div>
            <div className="flex items-start gap-2 rounded-lg px-3 py-2 text-[12px] mt-1" style={{ backgroundColor: "rgba(183,121,31,0.1)", color: "#8A5A12" }}>
              <Lock size={13} className="mt-0.5 shrink-0" /> Internal-only. This records the deal outcome for reporting — no payout formulas are applied.
            </div>
          </>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-[#D6E0E7] text-[#1C4358]">Cancel</Button>
          <Button data-testid="rev-submit" onClick={submit} disabled={saving || !cfg} className="bg-[#2F7D5A] hover:bg-[#276848] text-white gap-1">{saving && <Loader2 size={14} className="animate-spin" />} Save outcome</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
